<template>
  <div class="agent-composer"
    :class="stacked ? 'agent-composer--stacked' : 'agent-composer--inline d-flex align-items-center gap-2'"
    data-cy="agent-composer">
    <textarea ref="inputEl" v-model="draft" rows="1" class="agent-composer__input flex-grow-1"
      :placeholder="placeholder" :disabled="disabled" :aria-label="placeholder" maxlength="2000"
      data-cy="agent-composer-input" @input="autosize" @keydown.enter.exact.prevent="submit" />
    <div class="d-flex align-items-center gap-2 flex-shrink-0" :class="{ 'justify-content-end': stacked }">
      <button v-if="showExpand"
        class="agent-composer__icon-btn d-flex align-items-center justify-content-center flex-shrink-0" type="button"
        aria-label="Open the assistant" data-cy="agent-composer-expand" @click="$emit('expand')">
        <font-awesome-icon :icon="faUpRightAndDownLeftFromCenter" />
      </button>
      <button class="agent-composer__send d-flex align-items-center justify-content-center flex-shrink-0" type="button"
        :disabled="disabled || !draft.trim()" aria-label="Send" data-cy="agent-composer-send" @click="submit">
        <font-awesome-icon :icon="faArrowUp" />
      </button>
    </div>
  </div>
</template>

<script setup>
import { nextTick, ref } from 'vue'
import { faArrowUp } from '@fortawesome/pro-solid-svg-icons'
import { faUpRightAndDownLeftFromCenter } from '@fortawesome/pro-regular-svg-icons'

/**
 * The message input, used in every panel state.
 * Emits finished messages; the draft is a v-model so it survives the panel swapping
 * composers btwn states. `inline` = collapsed bar, `stacked` = expanded panel.
 */
defineProps({
  placeholder: {
    type: String,
    default: 'Message…',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  /**
   * Show the expand affordance — used when the panel is collapsed.
   */
  showExpand: {
    type: Boolean,
    default: false,
  },
  /**
   * Card layout w/ the input above the controls — used in the expanded panel.
   */
  stacked: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['send', 'expand'])

const draft = defineModel({ type: String, default: '' })
const inputEl = ref(null)

// grow w/ content instead of scrolling; CSS max-height caps it
function autosize() {
  const el = inputEl.value

  if (!el) return

  el.style.height = 'auto'
  el.style.height = `${el.scrollHeight}px`
}

function submit() {
  const text = draft.value.trim()

  if (!text) return

  draft.value = ''
  emit('send', text)

  nextTick(autosize)
}

function focus() {
  inputEl.value?.focus()
}

defineExpose({ focus })
</script>

<style scoped lang="scss">
// Lendio styleguide values — not auto-injected into SFC style blocks.
$border-gray-100: #E5E5E5;
$type-300: #6B717A;
$background-50: #F6F6F6;

.agent-composer {
  &__input {
    font-size: 1rem;
    outline: none;
    resize: none;
    overflow-y: auto;
    max-height: 8rem;
    line-height: 1.4;

    &:disabled {
      background: transparent;
      color: $type-300;
    }
  }

  &--inline &__input {
    border: 1px solid $border-gray-100;
    border-radius: 999px;
    padding: 0.5rem 1rem;

    &:focus {
      border-color: darken($border-gray-100, 15%);
    }

    &:disabled {
      background: $background-50;
    }
  }

  &--stacked {
    border: 1px solid $border-gray-100;
    border-radius: 16px;
    padding: 0.625rem 0.75rem 0.625rem 1rem;

    &:focus-within {
      border-color: darken($border-gray-100, 15%);
    }

    .agent-composer__input {
      display: block;
      width: 100%;
      border: none;
      padding: 0 0 0.625rem;
    }
  }

  &__send,
  &__icon-btn {
    width: 2.25rem;
    height: 2.25rem;
    border: none;
    border-radius: 50%;
    background: $background-50;
    color: $type-300;

    &:disabled {
      opacity: 0.5;
    }
  }

  &__send {
    background: #000;
    color: #fff;
  }
}
</style>
